import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import type { RatingTextColor } from '@freelancer/ui/rating';
import { textVariants } from '@freelancer/ui/text';
import { FontColor, TextSize } from '@freelancer/ui/text';
import { CompletedJobsSize } from './completed-jobs.types';

@Component({
  standalone: false,
  selector: 'fl-completed-jobs',
  template: `
    <fl-icon
      class="mr-xxxsmall"
      [name]="'ui-chart-donut'"
      [size]="size"
      [sizeTablet]="sizeTablet"
      [sizeDesktop]="sizeDesktop"
      [sizeDesktopLarge]="sizeDesktopLarge"
      [color]="
        completeCounter === 0 ? IconColor.FOREGROUND : IconColor.SECONDARY
      "
      [useIconFont]="true"
      [fillIconFont]="true"
    ></fl-icon>

    <p [class]="completedTextClass">{{ completeCounter }}%</p>
  `,
  styleUrls: ['./completed-jobs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompletedJobsComponent implements OnInit, OnChanges {
  IconColor = IconColor;

  completedTextClass: string;

  private completedTextSize: TextSize = TextSize.BODY_16;
  private completedTextSizeTablet?: TextSize | undefined;
  private completedTextSizeDesktop?: TextSize | undefined;
  private completedTextSizeDesktopLarge?: TextSize | undefined;

  @Input() completeCounter = 0;
  @Input() textColor: RatingTextColor = 'foreground';

  @Input() size: CompletedJobsSize = IconSize.MID;
  @Input() sizeTablet?: CompletedJobsSize;
  @Input() sizeDesktop?: CompletedJobsSize;
  @Input() sizeDesktopLarge?: CompletedJobsSize;

  ngOnInit(): void {
    this.calculateStyles();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      'size' in changes ||
      'sizeTablet' in changes ||
      'sizeDesktop' in changes ||
      'sizeDesktopLarge' in changes ||
      'textColor' in changes
    ) {
      this.calculateStyles();
    }
  }

  private calculateStyles(): void {
    switch (this.size) {
      case IconSize.SMALL:
        this.completedTextSize = TextSize.BODY_16;
        break;
      default:
        this.completedTextSize = TextSize.BODY_16;
    }

    switch (this.sizeTablet) {
      case IconSize.SMALL:
        this.completedTextSizeTablet = TextSize.BODY_16;
        break;
      case IconSize.MID:
        this.completedTextSizeTablet = TextSize.BODY_16;
        break;
      default:
        this.completedTextSizeTablet = undefined;
    }

    switch (this.sizeDesktop) {
      case IconSize.SMALL:
        this.completedTextSizeDesktop = TextSize.BODY_16;
        break;
      case IconSize.MID:
        this.completedTextSizeDesktop = TextSize.BODY_16;
        break;
      default:
        this.completedTextSizeDesktop = undefined;
    }

    switch (this.sizeDesktopLarge) {
      case IconSize.SMALL:
        this.completedTextSizeDesktopLarge = TextSize.BODY_16;
        break;
      case IconSize.MID:
        this.completedTextSizeDesktopLarge = TextSize.BODY_16;
        break;
      default:
        this.completedTextSizeDesktopLarge = undefined;
    }

    this.completedTextClass = textVariants({
      color:
        this.textColor === 'foreground'
          ? FontColor.FOREGROUND
          : FontColor.LIGHT,
      size: this.completedTextSize,
      sizeTablet: this.completedTextSizeTablet,
      sizeDesktop: this.completedTextSizeDesktop,
      sizeDesktopLarge: this.completedTextSizeDesktopLarge,
    });
  }
}
